import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Sparkles, Heart, Award, Truck, MessageCircle, User, Package, LogOut, MapPin, Star, ChevronLeft, ChevronRight, Volume2, VolumeX, Mail, Phone, Send, CheckCircle, Settings, X, Bell } from 'lucide-react';

const ConfiguradorWizard = lazy(() =>
  import('@/components/configurador/ConfiguradorWizard').then(m => ({ default: m.ConfiguradorWizard }))
);
const AuthModal = lazy(() =>
  import('@/components/auth/AuthModal').then(m => ({ default: m.AuthModal }))
);
const UserDashboard = lazy(() =>
  import('@/components/user/UserDashboard').then(m => ({ default: m.UserDashboard }))
);
const LegalModal = lazy(() =>
  import('@/components/legal/LegalModal').then(m => ({ default: m.LegalModal }))
);
import { scheduleCleanup, hasPendingTempData, clearTempSession } from '@/services/tempSession';
import { useGender } from '@/contexts/GenderContext';
import { requestPushToken, onPushMessage } from '@/firebase';
import { toast, Toaster } from 'sonner';
import type { FiguraConfig } from '@/types';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useTranslation, Trans } from 'react-i18next';
import { AnonProposalListener } from '@/components/AnonProposalListener';
import i18n from '@/i18n';
import { SEO } from '@/components/SEO';
import { ProductSchema } from '@/components/ProductSchema';
import { FAQ } from '@/components/FAQ';
import { useHtmlLang } from '@/hooks/useHtmlLang';
import { BodasTeaser } from '@/components/BodasTeaser';
import { EscenariosTeaser } from '@/components/EscenariosTeaser';
import { LANDINGS } from '@/seo/landings';
import { track as trackEvento, trackBeacon, visitorId as visitorIdTrack } from '@/lib/track';
import { ExitIntentModal } from '@/components/ExitIntentModal';
import { obtenerRutaAdmin } from '@/lib/adminPath';
import { DRAFT_MAX_AGE_MS, capturarReferido, decideExitModal, hasDraftInProgress, markExitModalShown, type ExitModalKind } from '@/lib/exitOffer';

function isVideoUrl(url: string) {
  return /\.(mp4|webm|ogg|mov|gif)(\?|$)/i.test(url);
}

function Header({
  onAuthClick,
  isLoggedIn,
  onLogout,
  onDashboardClick,
  isAdmin,
  hasStock,
  logoUrl,
  logoTextoUrl,
  onCartClick,
  onCartDiscard,
  cartInfo,
  unreadCount,
  onNotifClick,
}: {
  onAuthClick: () => void;
  isLoggedIn: boolean;
  onLogout: () => void;
  onDashboardClick: () => void;
  isAdmin: boolean;
  hasStock?: boolean;
  logoUrl?: string | null;
  logoTextoUrl?: string | null;
  onCartClick?: () => void;
  onCartDiscard?: () => void;
  cartInfo?: { tipo: string } | null;
  unreadCount?: number;
  onNotifClick?: () => void;
}) {
  const [hasTemp, setHasTemp] = useState(false);
  // Enlace al panel: el servidor solo da la ruta a cuentas admin.
  const [rutaAdmin, setRutaAdmin] = useState<string | null>(null);

  useEffect(() => {
    setHasTemp(hasPendingTempData());
  }, []);
  useEffect(() => {
    if (isAdmin) obtenerRutaAdmin().then(setRutaAdmin);
  }, [isAdmin]);

  const { t } = useTranslation();
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b border-[#D4AF37]/20" style={{ backgroundColor: 'color-mix(in srgb, var(--page-bg) 95%, transparent)' }}>
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4AF37]" aria-label="Funcomunión - Inicio">
            {/* Icono logo (redondo) */}
            {logoUrl ? (
              isVideoUrl(logoUrl)
                ? <video src={logoUrl} autoPlay muted loop playsInline className="w-10 h-10 rounded-full object-cover flex-shrink-0" aria-hidden="true" />
                : <img src={logoUrl} alt="" className="w-20 h-20 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
            )}
            {/* Logotexto — hidden on mobile to leave space for buttons */}
            {logoTextoUrl ? (
              <img src={logoTextoUrl} alt="" className="hidden sm:block h-20 w-auto object-contain" />
            ) : (
              <span className="hidden sm:inline text-2xl font-semibold tracking-wide" style={{ fontFamily: 'Cormorant Garamond, serif' }} aria-hidden="true">
                <span style={{ color: 'var(--page-heading)' }}>FUNCO</span><span className="text-gradient-gold">MUNION</span>
              </span>
            )}
          </a>
          
          <nav className="hidden md:flex items-center gap-10">
            {[
              { href: '#como-funciona', label: t('nav.howItWorks') },
              ...(hasStock ? [{ href: '#disponibles', label: t('stock.navLink') }] : []),
              { href: '#faq', label: 'FAQ' },
              { href: '#sobre-nosotros', label: t('nav.about') },
            ].map(({ href, label }) => (
              <a
                key={href}
                href={href}
                className="relative text-[15px] font-medium tracking-[0.07em] transition-colors group"
                style={{ color: 'var(--page-heading)', fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              >
                {label}
                <span className="absolute -bottom-0.5 left-0 w-0 h-[1.5px] bg-[#D4AF37] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>
          
          <div className="flex items-center gap-3">
            <LanguageSwitcher compact />
            {cartInfo && onCartClick && (
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={onCartClick}
                  className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37] rounded-l-full hover:bg-[#D4AF37]/20 transition-all text-xs font-medium"
                  aria-label={t('home.headerCartResume')}
                  title={t('home.headerCartResume')}
                >
                  <Package className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline">{t('home.headerCartResumeShort')} {cartInfo.tipo}</span>
                  <span className="sm:hidden">{t('home.headerCartResumeShort')}</span>
                </button>
                {onCartDiscard && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onCartDiscard(); }}
                    className="px-1.5 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/40 border-l-0 text-[#D4AF37]/60 rounded-r-full hover:bg-red-50 hover:text-red-400 transition-all"
                    aria-label={t('home.headerCartDiscard')}
                    title={t('home.headerCartDiscard')}
                  >
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                )}
              </div>
            )}

            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                {isAdmin && rutaAdmin && (
                  <a
                    href={rutaAdmin}
                    className="flex items-center gap-2 p-2 sm:px-4 sm:py-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all text-sm font-medium"
                    aria-label={t('home.headerAdmin')}
                    title={t('home.headerAdmin')}
                  >
                    <Settings className="w-4 h-4 sm:hidden" aria-hidden="true" />
                    <span className="hidden sm:inline">{t('home.headerAdmin')}</span>
                  </a>
                )}
                {!!unreadCount && unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onNotifClick?.(); }}
                    className="relative p-2 rounded-full hover:bg-[#D4AF37]/10 transition-colors"
                    aria-label={unreadCount > 1 ? t('home.headerNotifTitlePlural', { count: unreadCount }) : t('home.headerNotifTitle', { count: unreadCount })}
                    title={unreadCount > 1 ? t('home.headerNotifTitlePlural', { count: unreadCount }) : t('home.headerNotifTitle', { count: unreadCount })}
                  >
                    <Bell className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" />
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold" aria-hidden="true">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={onDashboardClick}
                  className="flex items-center gap-2 p-2 sm:px-4 sm:py-2 rounded-full text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: 'var(--page-heading)' }}
                  aria-label={t('home.headerMyAccount')}
                  title={t('home.headerMyAccount')}
                >
                  <User className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline text-sm">{t('home.headerMyAccount')}</span>
                </button>
                <button
                  type="button"
                  onClick={onLogout}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label={t('home.headerLogout')}
                  title={t('home.headerLogout')}
                >
                  <LogOut className="w-4 h-4 text-[#6B7280]" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={onAuthClick}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 transition-all"
                style={{ color: 'var(--page-heading)' }}
              >
                <User className="w-4 h-4" aria-hidden="true" />
                <span className="text-sm font-medium">{t('nav.login')}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

type HeroItem = { url: string; isVideo: boolean; noBgUrl?: string; genero?: 'nino' | 'nina'; useNoBg?: boolean };

function HeroCarousel({ onStart }: { onStart: () => void }) {
  const { genero: activeGenero } = useGender();
  const [media, setMedia] = useState<HeroItem[]>([]);
  const [idx, setIdx] = useState(0);
  const [loopKey, setLoopKey] = useState(0); // remounts single video on loop
  const [volume, setVolume] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    fetch('/api/site/hero-images')
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setMedia((d.items || []).filter((it: any) => !it.hidden)))
      .catch(() => {});
  }, []);

  const videoKey = media.length > 1 ? idx : loopKey;

  // Sync volume state → video DOM
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = volume;
    v.muted = volume === 0;
  }, [volume]);

  // 5s one-shot timeout for image items; video uses onEnded
  useEffect(() => {
    if (media.length <= 1) return;
    if (media[idx]?.isVideo) return;
    const t = setTimeout(() => {
      setIdx(i => media[i]?.isVideo ? i : (i + 1) % media.length);
    }, 5000);
    return () => clearTimeout(t);
  }, [media, idx]);

  const badges = (
    <>
      <div className="absolute -top-4 -right-4 px-4 py-2 bg-white rounded-full shadow-elegant border border-[#D4AF37]/30">
        <span className="text-sm font-medium" style={{ color: 'var(--page-heading)' }}>100% Personalizable</span>
      </div>
      <div className="absolute -bottom-4 -left-4 px-4 py-2 rounded-full shadow-elegant" style={{ background: 'linear-gradient(to right, var(--page-accent), var(--page-heading))' }}>
        <span className="text-sm font-medium text-white">Pintado a mano</span>
      </div>
    </>
  );

  if (!media.length) {
    return (
      <div className="relative">
        <div className="aspect-square rounded-3xl bg-gradient-to-br from-[#F0EDE8] to-white p-2 shadow-elegant-lg">
          <div className="w-full h-full rounded-2xl bg-white flex items-center justify-center relative overflow-hidden cursor-pointer" onClick={onStart}>
            <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent" />
            <div className="text-center">
              <div className="w-48 h-48 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#1E3A5F]/10 flex items-center justify-center">
                <Sparkles className="w-24 h-24 text-[#D4AF37]" />
              </div>
              <p className="text-[#6B7280] italic">Tu figura personalizada aqui</p>
            </div>
          </div>
        </div>
        {badges}
      </div>
    );
  }

  const current = media[idx];

  return (
    <div className="relative">
      <div className="aspect-square rounded-3xl overflow-hidden shadow-elegant-lg cursor-pointer relative" onClick={onStart}>
        {current.isVideo ? (
          <>
            <video
              ref={videoRef}
              key={videoKey}
              src={current.url}
              playsInline
              muted
              autoPlay
              onEnded={() => {
                if (media.length > 1) setIdx(i => (i + 1) % media.length);
                else setLoopKey(k => k + 1);
              }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div
              className="absolute bottom-3 right-3 z-10 flex items-center gap-2 rounded-xl bg-black/60 px-3 py-2"
              style={{ backdropFilter: 'blur(4px)' }}
              onClick={e => e.stopPropagation()}
            >
              {volume === 0
                ? <VolumeX className="w-5 h-5 text-white flex-shrink-0" />
                : <Volume2 className="w-5 h-5 text-white flex-shrink-0" />}
              <input
                type="range"
                aria-label="Volumen"
                min={0} max={1} step={0.05}
                value={volume}
                onChange={e => {
                  const v = videoRef.current;
                  const val = parseFloat(e.target.value);
                  setVolume(val);
                  if (v) { v.volume = val; v.muted = val === 0; }
                }}
                style={{ width: '80px', accentColor: '#D4AF37' }}
              />
            </div>
          </>
        ) : current.noBgUrl && current.useNoBg !== false ? (
          /* No-bg image with gender-aware background */
          <div key={current.url} className="absolute inset-0 w-full h-full flex items-center justify-center px-8 py-10 transition-colors duration-500"
            style={{ background: (() => {
              const g = current.genero || activeGenero;
              return g === 'nina'
                ? 'radial-gradient(ellipse at 50% 40%, #FFF0F5 0%, #FDF2F8 50%, #FCE7F3 100%)'
                : 'radial-gradient(ellipse at 50% 40%, #FFF8ED 0%, #F5F0E6 50%, #EDE8DA 100%)';
            })() }}>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[60%] h-[60%] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(255,220,170,0.3) 0%, transparent 65%)', filter: 'blur(4px)' }} />
            </div>
            <img
              src={current.noBgUrl}
              alt="Figura de comunión personalizada"
              className="relative z-10 h-full w-full object-contain"
              style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.1))' }}
            />
          </div>
        ) : (
          <img
            key={current.url}
            src={current.url}
            alt="Figura de comunión personalizada"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-contain bg-white"
          />
        )}
        {media.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-0 z-10">
            {media.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Imagen ${i + 1}`}
                aria-current={i === idx ? 'true' : undefined}
                onClick={e => { e.stopPropagation(); setIdx(i); }}
                className={`box-content p-2 bg-clip-content h-2 rounded-full transition-all duration-300 ${i === idx ? 'bg-[#D4AF37] w-6' : 'bg-white/70 w-2'}`}
              />
            ))}
          </div>
        )}
      </div>
      {badges}
    </div>
  );
}

function Hero({ onStart }: { onStart: () => void }) {
  const { t } = useTranslation();
  return (
    <section className="min-h-screen flex items-center relative overflow-hidden sparkle-field pt-28 pb-8 md:pt-40 md:pb-16">
      {/* Organic background shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-16 right-16 w-[500px] h-[500px] rounded-full blur-[100px] opacity-[0.04]" style={{ backgroundColor: 'var(--page-accent)' }} />
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full blur-[80px] opacity-[0.05]" style={{ backgroundColor: '#D4AF37' }} />
        {/* Floating decorative symbols */}
        <span className="absolute top-[20%] left-[5%] text-[#D4AF37]/10 text-2xl animate-float-slow select-none">✦</span>
        <span className="absolute top-[35%] right-[8%] text-[#D4AF37]/[0.07] text-lg animate-float select-none" style={{ animationDelay: '1s' }}>✧</span>
        <span className="absolute bottom-[30%] left-[15%] text-[#D4AF37]/[0.08] text-sm animate-float select-none" style={{ animationDelay: '2s' }}>✦</span>
        {/* Curved decorative line */}
        <svg className="absolute bottom-0 left-0 right-0 w-full opacity-[0.06]" height="80" viewBox="0 0 1440 80" preserveAspectRatio="none">
          <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z" fill="currentColor" style={{ color: 'var(--page-accent)' }} />
        </svg>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 backdrop-blur-sm">
              <span className="text-[#D4AF37] text-sm">✦</span>
              <span className="text-sm tracking-wide" style={{ color: 'var(--page-heading)', fontFamily: 'Cormorant Garamond, serif' }}>{t('hero.badge')}</span>
              <span className="text-[#D4AF37] text-sm">✦</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.1]" style={{ color: 'var(--page-heading)' }}>
              {t('hero.titleLine1')} <br />
              <span className="text-gradient-gold italic">{t('hero.titleHighlight')}</span> <br />
              {t('hero.titleLine2')}
            </h1>

            <p className="text-lg text-[#6B7280] max-w-lg leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.25rem' }}>
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={onStart}
                className="btn-nacre px-8 py-4 rounded-full font-medium shadow-elegant-lg hover:shadow-xl transition-all hover:scale-[1.03] group"
              >
                <span className="flex items-center gap-2">
                  {t('hero.ctaPrimary')}
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </button>
              <a
                href="#como-funciona"
                className="px-8 py-4 rounded-full font-medium transition-all hover:opacity-80"
                style={{ border: '1px solid color-mix(in srgb, var(--page-accent) 20%, transparent)', color: 'var(--page-accent)' }}
              >
                {t('hero.ctaSecondary')}
              </a>
            </div>

            <div className="flex items-center gap-4 sm:gap-8 pt-4 flex-wrap">
              {[
                ['500+', t('hero.statFigures')],
                ['4.9 ★', t('hero.statRating')],
                ['48h', t('hero.statShipping')],
              ].map(([num, label], i) => (
                <div key={i} className="text-center flex items-center gap-3 sm:gap-6">
                  {i > 0 && <div className="w-px h-10 bg-[#D4AF37]/15 hidden sm:block" />}
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-gradient-gold">{num}</div>
                    <div className="text-[10px] sm:text-xs text-[#6B7280] tracking-wide">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <HeroCarousel onStart={onStart} />
        </div>
      </div>
    </section>
  );
}

function ComoFunciona() {
  const { t } = useTranslation();
  const pasos = [
    { num: '01', titulo: t('howItWorks.step1Title'), desc: t('howItWorks.step1Desc'), emoji: '✏️' },
    { num: '02', titulo: t('howItWorks.step2Title'), desc: t('howItWorks.step2Desc'), emoji: '🎨' },
    { num: '03', titulo: t('howItWorks.step3Title'), desc: t('howItWorks.step3Desc'), emoji: '🖌️' },
    { num: '04', titulo: t('howItWorks.step4Title'), desc: t('howItWorks.step4Desc'), emoji: '🎁' },
  ];

  return (
    <section id="como-funciona" className="py-24 bg-white relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, var(--page-accent) 1px, transparent 1px), radial-gradient(circle at 80% 20%, #D4AF37 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="container mx-auto px-6 relative">
        <div className="text-center mb-16">
          <div className="divider-ornament max-w-xs mx-auto mb-4">✦ ✦ ✦</div>
          <h2 className="text-4xl md:text-5xl mt-3" style={{ color: 'var(--page-heading)' }}>{t('howItWorks.title')}</h2>
          <p className="text-[#6B7280] mt-4 max-w-md mx-auto" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.15rem' }}>
            {t('howItWorks.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 lg:gap-8">
          {pasos.map((paso, i) => (
            <div key={i} className="relative text-center group">
              {/* Connector line between steps (hidden on mobile) */}
              {i < 3 && (
                <div className="hidden md:block absolute top-10 left-[60%] right-[-40%] h-px z-0">
                  <div className="w-full h-full border-t border-dashed border-[#D4AF37]/20" />
                </div>
              )}
              <div className="card-boutique p-6 relative z-10">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 flex items-center justify-center halo-gold">
                  <span className="text-2xl">{paso.emoji}</span>
                </div>
                <span className="text-xs font-bold text-[#D4AF37]/30 tracking-widest">{paso.num}</span>
                <h3 className="text-xl font-semibold mt-1 mb-2" style={{ color: 'var(--page-heading)' }}>{paso.titulo}</h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">{paso.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Precios({ onSelect }: { onSelect: (tipo: string) => void }) {
  const { t } = useTranslation();
  const defaults = { mini: 24.90, classic: 34.90, premium: 49.90, pareja: 59.90, llavero: 4.90 };
  const [prices, setPrices] = useState(defaults);
  const [productImages, setProductImages] = useState<Record<string, string>>({});
  const [lightbox, setLightbox] = useState<null | typeof planes[0]>(null);

  useEffect(() => {
    fetch('/api/site/prices').then(r => r.ok ? r.json() : {}).then(data => {
      if (data && Object.keys(data).length) setPrices(p => ({ ...p, ...data }));
    }).catch(() => {});
    fetch('/api/site/product-images').then(r => r.ok ? r.json() : {}).then(d => {
      if (d && typeof d === 'object') setProductImages(d);
    }).catch(() => {});
  }, []);

  /* Product visual — real image if available, otherwise proportional silhouette */
  const FiguraVisual = ({ plan }: { plan: typeof planes[0] }) => {
    const img = productImages[plan.key];
    const h = plan.figH;

    if (img) {
      return (
        <div className="mx-auto mb-4 relative flex items-center justify-center" style={{ minHeight: `${h}px` }}>
          {/* Warm halo */}
          <div className="absolute inset-0 -m-4 flex items-center justify-center pointer-events-none">
            <div className="w-full h-full rounded-full"
              style={{ background: plan.premium
                ? 'radial-gradient(circle, rgba(255,220,170,0.45) 0%, transparent 65%)'
                : 'radial-gradient(circle, rgba(255,220,170,0.2) 0%, transparent 65%)',
                filter: 'blur(2px)' }} />
          </div>
          <img
            src={img}
            alt={t('home.figureImageAlt', { nombre: plan.nombre })}
            className={`mx-auto w-auto object-contain relative z-10 ${plan.premium ? 'animate-float' : ''}`}
            style={{ maxHeight: `${plan.imgMax}px`, filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.08))' }}
          />
        </div>
      );
    }

    // Fallback: silhouette
    return (
      <div className="mx-auto mb-4 flex items-end justify-center relative" style={{ height: `${h}px` }}>
        {plan.premium && (
          <div className="absolute inset-0 -m-6 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(255,220,170,0.35) 0%, transparent 65%)', filter: 'blur(2px)' }} />
        )}
        {plan.pareja ? (
          <div className="flex items-end gap-2 relative z-10">
            {[0, 1].map(n => (
              <svg key={n} viewBox="0 0 40 80" style={{ height: `${h * 0.8}px` }} className="opacity-[0.12]" fill="currentColor" color="var(--page-heading)">
                <ellipse cx="20" cy="16" rx="14" ry="16" /><rect x="8" y="30" width="24" height="35" rx="8" /><rect x="6" y="65" width="28" height="6" rx="3" />
              </svg>
            ))}
          </div>
        ) : (
          <svg viewBox="0 0 40 80" style={{ height: `${h}px` }} className={`relative z-10 ${plan.premium ? 'opacity-[0.18]' : 'opacity-[0.10]'}`}
            fill="currentColor" color={plan.premium ? '#D4AF37' : 'var(--page-heading)'}>
            <ellipse cx="20" cy="16" rx="14" ry="16" /><rect x="8" y="30" width="24" height="35" rx="8" /><rect x="6" y="65" width="28" height="6" rx="3" />
          </svg>
        )}
      </div>
    );
  };

  const planes = [
    { key: 'mini', nombre: 'Mini', precio: prices.mini, altura: '7 cm', desc: t('home.miniDesc'), figH: 50, imgMax: 150 },
    { key: 'classic', nombre: 'Classic', precio: prices.classic, altura: '10 cm', desc: t('home.classicDesc'), badge: t('home.classicBadge'), figH: 70, imgMax: 170 },
    { key: 'premium', nombre: 'Premium', precio: prices.premium, altura: '15 cm', desc: t('home.premiumDesc'), badge: t('home.premiumBadge'), figH: 130, premium: true, imgMax: 180 },
    { key: 'pareja', nombre: 'Pareja', precio: prices.pareja, altura: '2 × 10 cm', desc: t('home.parejaDesc'), figH: 70, pareja: true, imgMax: 140 },
  ];

  return (
    <section id="precios" className="py-24 relative overflow-hidden">
      {/* Atmospheric warm lighting */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 50% 40%, rgba(255,240,220,0.55) 0%, transparent 60%), linear-gradient(180deg, var(--page-bg) 0%, color-mix(in srgb, var(--page-bg) 92%, #D4AF37) 100%)' }} />

      <div className="container mx-auto px-6 relative">
        <div className="text-center mb-16">
          <div className="divider-ornament max-w-xs mx-auto mb-4">✦ ✦ ✦</div>
          <h2 className="text-4xl md:text-5xl mt-3" style={{ color: 'var(--page-heading)' }}>{t('home.priceTitle')}</h2>
          <p className="text-[#6B7280] mt-4 max-w-md mx-auto" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.15rem' }}>
            {t('home.priceSubtitle')}
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-5 py-2 bg-green-50 border border-green-200 rounded-full">
            <span className="text-green-700 text-sm font-semibold">{t('home.promo2026')}</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:items-end">
          {planes.map((plan) => (
            <div
              key={plan.nombre}
              className={`card-boutique transition-all ${
                plan.premium
                  ? 'p-9 lg:-mt-10 border-[#D4AF37]/30 ring-2 ring-[#D4AF37]/10'
                  : 'p-7'
              }`}
              style={plan.premium
                ? { boxShadow: '0 16px 56px rgba(212,175,55,0.22), 0 4px 20px rgba(0,0,0,0.05)', background: 'radial-gradient(ellipse at 50% 20%, rgba(255,248,230,0.9) 0%, white 50%, #FFFDF7 100%)' }
                : undefined}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className={`px-4 py-1.5 rounded-full text-xs font-medium text-white flex items-center gap-1.5 ${
                    plan.premium
                      ? 'bg-gradient-to-r from-[#D4AF37] via-[#E8C94A] to-[#B8941F] shadow-lg'
                      : 'bg-[#D4AF37]/60 shadow-sm'
                  }`}>
                    {plan.premium && <span className="text-[10px]">✦</span>}
                    {plan.badge}
                    {plan.premium && <span className="text-[10px]">✦</span>}
                  </span>
                </div>
              )}
              <div className="text-center relative">
                {/* Product visual — clickable for lightbox */}
                <div className={`cursor-pointer group/img ${productImages[plan.key] ? 'hover:scale-105 transition-transform' : ''}`}
                  onClick={() => setLightbox(plan)}>
                  <FiguraVisual plan={plan} />
                  {productImages[plan.key] && (
                    <span className="opacity-0 group-hover/img:opacity-100 transition-opacity absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/40 text-white text-[10px] px-3 py-1 rounded-full backdrop-blur-sm pointer-events-none z-20">
                      {t('home.viewDetail')}
                    </span>
                  )}
                </div>

                <h3 className={`font-semibold ${plan.premium ? 'text-2xl' : 'text-xl'}`}
                  style={{ color: plan.premium ? 'var(--page-heading)' : 'color-mix(in srgb, var(--page-heading) 75%, transparent)' }}>{plan.nombre}</h3>
                <p className={`text-xs mt-1 tracking-wide ${plan.premium ? 'text-[#6B7280]' : 'text-[#9CA3AF]'}`}>~{plan.altura} <span className="text-[10px] opacity-70">{t('home.aprox')}</span></p>
                <div className={`mt-3 mb-2 ${plan.premium ? 'my-4' : ''}`}>
                  {plan.premium && <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider block mb-0.5">{t('home.from')}</span>}
                  <span className={`font-bold text-gradient-gold ${plan.premium ? 'text-5xl' : 'text-4xl'}`}
                    style={!plan.premium ? { opacity: 0.75 } : undefined}>{plan.precio % 1 === 0 ? plan.precio : plan.precio.toFixed(2).replace('.', ',')}</span>
                  <span className="text-[#9CA3AF] text-sm ml-1">€</span>
                  <span className="block text-[10px] text-green-600 font-medium mt-0.5">{t('home.vatIncluded')}</span>
                </div>
                <p className={`leading-relaxed ${plan.premium ? 'text-sm text-[#6B7280] mb-1' : 'text-sm text-[#9CA3AF] mb-6'}`} style={{ fontFamily: 'Cormorant Garamond, serif' }}>{plan.desc}</p>

                {/* Premium: emotional sub-line + micro-details */}
                {plan.premium && (
                  <>
                    <p className="text-sm text-[#B8941F] font-semibold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {t('home.premiumSubline')}
                    </p>
                    <div className="flex flex-col items-center gap-0.5 mb-3 text-[11px] text-[#9CA3AF]">
                      {[t('home.featSize'), t('home.featPresence'), t('home.featDetail')].map(featLabel => (
                        <span key={featLabel} className="flex items-center gap-1.5">
                          <span className="text-[#D4AF37]">✓</span> {featLabel}
                        </span>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#B8941F]/50 mb-3 flex items-center justify-center gap-1">
                      <span className="text-[#D4AF37]">✔</span> {t('home.featCustom')}
                    </p>
                  </>
                )}

                <button
                  onClick={() => onSelect(plan.nombre)}
                  className={`w-full rounded-full font-medium transition-all group ${
                    plan.premium
                      ? 'py-3 btn-nacre'
                      : 'py-2.5 text-sm hover:opacity-90'
                  }`}
                  style={plan.premium
                    ? { boxShadow: '0 10px 25px rgba(200,150,80,0.35)' }
                    : { border: '1px solid color-mix(in srgb, var(--page-heading) 15%, transparent)', color: 'color-mix(in srgb, var(--page-heading) 70%, transparent)' }}
                >
                  <span className="flex items-center justify-center gap-2">
                    {plan.premium ? t('home.priceCreatePremium') : t('home.priceCreate')}
                    <span className="group-hover:translate-x-1 transition-transform text-sm">→</span>
                  </span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Llaveros — regalo para invitados */}
        <div className="mt-12 max-w-2xl mx-auto">
          <div className="card-boutique p-8 border-[#D4AF37]/30">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
              <span className="px-4 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#B8941F] rounded-full text-xs font-medium text-white shadow-md flex items-center gap-1.5">
                {t('home.kRingForGuests')}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-8">
              {/* Llavero product image */}
              {productImages.llavero && (
                <div className="flex-shrink-0 relative">
                  <div className="absolute inset-0 -m-3 rounded-full pointer-events-none"
                    style={{ background: 'radial-gradient(circle, rgba(255,220,170,0.25) 0%, transparent 65%)', filter: 'blur(2px)' }} />
                  <img src={productImages.llavero} alt={t('home.kRingAlt')} className="w-24 h-24 object-contain relative z-10"
                    style={{ filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.06))' }} />
                </div>
              )}
              <div className="text-center sm:text-left flex-1">
                <h3 className="text-2xl font-semibold" style={{ color: 'var(--page-heading)' }}>{t('home.kRingTitle')}</h3>
                <p className="text-sm text-[#6B7280] mt-1 mb-4">{t('home.kRingSubtitle')}</p>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  <Trans i18nKey="home.kRingDesc" components={{ strong: <strong /> }} />
                </p>
              </div>
              <div className="text-center flex-shrink-0">
                <div className="mb-1">
                  <span className="text-5xl font-bold text-gradient-gold">{prices.llavero % 1 === 0 ? prices.llavero : prices.llavero.toFixed(2).replace('.', ',')}</span>
                  <span className="text-[#6B7280] text-lg">€</span>
                </div>
                <p className="text-xs text-[#6B7280] mb-1">{t('home.kRingPerUnit')}</p>
                <p className="text-xs text-[#D4AF37] font-medium mb-4">{t('home.kRingMinOrder', { total: (prices.llavero * 20) % 1 === 0 ? (prices.llavero * 20) : (prices.llavero * 20).toFixed(2).replace('.', ',') })}</p>
                <button
                  onClick={() => onSelect('Llavero')}
                  className="px-8 py-3 rounded-full font-medium transition-all hover:opacity-90 btn-nacre"
                >
                  {t('home.kRingOrder')}
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="text-[11px] text-[#9CA3AF] text-center mt-8 italic max-w-2xl mx-auto">
          <Trans i18nKey="home.priceSizeNote" components={{ strong: <strong /> }} />
        </p>

        <p className="text-[11px] text-[#9CA3AF] text-center mt-3 italic max-w-2xl mx-auto">
          🌍 {t('home.intlShipping')}
        </p>
      </div>

      {/* ── Lightbox modal ── */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          <div
            className="relative max-w-md w-full rounded-3xl overflow-hidden shadow-2xl animate-fade-in-up"
            onClick={e => e.stopPropagation()}
            style={{ background: 'radial-gradient(ellipse at 50% 25%, rgba(255,245,225,0.95) 0%, white 55%, #FAFAF8 100%)' }}
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center text-gray-400 text-sm transition-colors"
            >
              ✕
            </button>

            {/* Product image — hero zone */}
            <div className="relative pt-6 pb-2 px-6">
              {/* Warm halo behind product */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[70%] h-[70%] rounded-full"
                  style={{ background: 'radial-gradient(circle, rgba(255,220,170,0.45) 0%, transparent 65%)', filter: 'blur(2px)' }} />
              </div>
              <div className="flex justify-center relative z-10" style={{ minHeight: '200px' }}>
                {productImages[lightbox.key] ? (
                  <img
                    src={productImages[lightbox.key]}
                    alt={lightbox.nombre}
                    className="max-h-[55vh] w-auto object-contain"
                    style={{ filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.08))' }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-[180px]">
                    <svg viewBox="0 0 40 80" className="h-[140px] opacity-[0.12]" fill="currentColor" color="var(--page-heading)">
                      <ellipse cx="20" cy="16" rx="14" ry="16" /><rect x="8" y="30" width="24" height="35" rx="8" /><rect x="6" y="65" width="28" height="6" rx="3" />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* Compact details */}
            <div className="px-6 pb-6 text-center">
              {lightbox.badge && (
                <span className={`inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-[10px] font-medium text-white mb-2 ${
                  lightbox.premium
                    ? 'bg-gradient-to-r from-[#D4AF37] via-[#E8C94A] to-[#B8941F]'
                    : 'bg-[#D4AF37]/60'
                }`}>
                  {lightbox.premium && <span className="text-[8px]">✦</span>}
                  {lightbox.badge}
                  {lightbox.premium && <span className="text-[8px]">✦</span>}
                </span>
              )}

              <h3 className="text-2xl font-semibold" style={{ color: 'var(--page-heading)' }}>{lightbox.nombre}</h3>
              <p className="text-xs text-[#9CA3AF] mt-0.5">{lightbox.altura} · {t('home.handPainted')}</p>

              <div className="mt-3 mb-2">
                <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">{t('home.from')}</span>
                <div>
                  <span className="text-5xl font-bold text-gradient-gold">
                    {lightbox.precio % 1 === 0 ? lightbox.precio : lightbox.precio.toFixed(2).replace('.', ',')}
                  </span>
                  <span className="text-[#9CA3AF] text-sm ml-1">€</span>
                  <span className="block text-[10px] text-green-600 font-medium mt-0.5">{t('home.vatIncluded')}</span>
                </div>
              </div>

              <p className="text-sm text-[#6B7280] mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {lightbox.desc}
              </p>

              {lightbox.premium && (
                <>
                  <p className="text-sm text-[#B8941F] font-semibold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {t('home.premiumSubline')}
                  </p>
                  <div className="flex justify-center gap-4 mb-3 text-[11px] text-[#9CA3AF]">
                    {[t('home.featSize'), t('home.featPresence'), t('home.featDetail')].map(featLabel => (
                      <span key={featLabel} className="flex items-center gap-1">
                        <span className="text-[#D4AF37]">✓</span> {featLabel}
                      </span>
                    ))}
                  </div>
                </>
              )}

              <p className="text-[10px] text-[#B8941F]/50 mb-3 flex items-center justify-center gap-1">
                <span className="text-[#D4AF37]">✔</span> {t('home.featCustom')}
              </p>

              <button
                onClick={() => { setLightbox(null); onSelect(lightbox.nombre); }}
                className="w-full py-3 rounded-full font-medium btn-nacre transition-all hover:scale-[1.02] group"
                style={{ boxShadow: '0 10px 25px rgba(200,150,80,0.35)' }}
              >
                <span className="flex items-center justify-center gap-2">
                  {lightbox.premium ? t('home.priceCreatePremium') : t('home.priceCreate')}
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Reseñas() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'es').split('-')[0];
  const [reviews, setReviews] = useState<any[]>([]);
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Cache-buster: la decisión de pixelar puede cambiar en cualquier
    // momento desde el panel admin. Pedimos siempre fresco para que el
    // visitante vea la versión actual.
    fetch(`/api/reviews/featured?t=${Date.now()}`, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setReviews(data); setCurrent(0); })
      .catch(() => {});
  }, []);

  // Cuando cambia el idioma o la reseña actual, busca traducción si no es es.
  useEffect(() => {
    if (lang === 'es' || reviews.length === 0) return;
    const r = reviews[current];
    if (!r?.id || !r?.text) return;
    // Cache local: si ya la tradujimos, no llames otra vez.
    const cacheKey = `${r.id}:${lang}`;
    if (translations[cacheKey] !== undefined) return;
    // Si el doc ya trae translations[lang] del backend, úsalo directo.
    if (r.translations?.[lang]) {
      setTranslations(prev => ({ ...prev, [cacheKey]: r.translations[lang] }));
      return;
    }
    fetch('/api/reviews/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: r.id, lang }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(d => {
        if (d?.text) setTranslations(prev => ({ ...prev, [cacheKey]: d.text }));
      })
      .catch(() => {});
  }, [lang, current, reviews]);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % Math.max(reviews.length, 1));
    }, 4000);
  };

  useEffect(() => {
    if (reviews.length < 2) return;
    startTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [reviews.length]);

  const goTo = (idx: number) => {
    setCurrent(idx);
    startTimer();
  };
  const prev = () => goTo((current - 1 + reviews.length) % reviews.length);
  const next = () => goTo((current + 1) % reviews.length);

  if (reviews.length === 0) return null;

  const review = reviews[current];
  const isNina = review?.genero === 'nina';
  const accent = isNina ? '#BE185D' : '#1E3A5F';
  const cardBg = isNina ? '#FDF2F8' : '#F5F0E6';
  const borderCol = isNina ? '#FBCFE8' : '#D4AF3740';

  return (
    <section id="reseñas" className="py-20" style={{ backgroundColor: 'var(--page-bg)' }}>
      <div className="container mx-auto px-6 max-w-3xl">
        {/* Heading */}
        <div className="text-center mb-12">
          <div className="divider-ornament max-w-xs mx-auto mb-4">✦ ✦ ✦</div>
          <h2 className="text-3xl md:text-4xl font-semibold" style={{ color: 'var(--page-heading)' }}>
            {t('home.reviewsTitle')}
          </h2>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div
            className="card-boutique p-6 md:p-8 transition-all duration-500"
            style={{ backgroundColor: cardBg, borderColor: borderCol }}
          >
            <div className={`flex ${Array.isArray(review?.images) && review.images.length > 0 ? 'flex-col md:flex-row gap-6 items-start' : 'flex-col'}`}>
              {/* Foto a la izquierda — tamaño fijo (aspect 4:5) */}
              {Array.isArray(review?.images) && review.images.length > 0 && (
                <div className="flex-shrink-0 w-full md:w-[260px]">
                  {/* Foto principal — aspecto 4:5 fijo para todas las reseñas */}
                  <button
                    onClick={() => setLightbox(review.images[0].full || review.images[0].thumb)}
                    className="w-full aspect-[4/5] rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow block bg-gray-100"
                  >
                    <img
                      src={review.images[0].full || review.images[0].thumb}
                      alt={t('home.reviewPhotoAlt')}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </button>
                  {/* Thumbs adicionales si hay más de una */}
                  {review.images.length > 1 && (
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      {review.images.slice(1).map((img: any, i: number) => (
                        <button
                          key={i}
                          onClick={() => setLightbox(img.full || img.thumb)}
                          className="w-12 h-12 rounded overflow-hidden border border-white shadow hover:scale-105 transition-transform"
                        >
                          <img
                            src={img.thumb || img.full}
                            alt={t('home.reviewPhotoN', { n: i + 2 })}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Texto + estrellas a la derecha */}
              <div className="flex-1 min-w-0">
                {/* Stars */}
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className={`w-5 h-5 ${s <= (review?.rating || 5) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-300'}`} />
                  ))}
                </div>

                {/* Quote — usa traducción si idioma != es y disponible */}
                {(() => {
                  const cacheKey = review?.id && lang !== 'es' ? `${review.id}:${lang}` : null;
                  const translated = cacheKey ? translations[cacheKey] : null;
                  const display = translated || review?.text;
                  return display ? (
                    <div className="mb-4">
                      <p className="text-base md:text-lg leading-relaxed text-[#374151] italic">
                        "{display}"
                      </p>
                      {translated && (
                        <p className="mt-1 text-[10px] text-gray-400 not-italic">{t('home.reviewAutoTranslated')}</p>
                      )}
                    </div>
                  ) : null;
                })()}

                {/* Chip */}
                <div>
                  <span className="text-xs px-3 py-1 rounded-full font-medium inline-block"
                    style={{ backgroundColor: `${accent}15`, color: accent }}>
                    {isNina ? '👧' : '👦'} {review?.nombreNino || (isNina ? t('home.reviewGirl') : t('home.reviewBoy'))}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Prev / Next */}
          {reviews.length > 1 && (
            <>
              <button type="button" onClick={prev} aria-label={t('home.reviewPrev')}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 w-10 h-10 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center hover:border-[#D4AF37] transition-colors">
                <ChevronLeft className="w-5 h-5 text-[#6B7280]" aria-hidden="true" />
              </button>
              <button type="button" onClick={next} aria-label={t('home.reviewNext')}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 w-10 h-10 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center hover:border-[#D4AF37] transition-colors">
                <ChevronRight className="w-5 h-5 text-[#6B7280]" aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        {/* Dots */}
        {reviews.length > 1 && (
          <div className="flex justify-center gap-0 mt-6" role="tablist" aria-label={t('home.reviewsTitle')}>
            {reviews.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                role="tab"
                aria-selected={i === current}
                aria-label={t('home.reviewGoTo', { n: i + 1 })}
                className={`box-content p-2 bg-clip-content h-2 rounded-full transition-all duration-300 ${i === current ? 'w-5' : 'w-2'}`}
                style={{ backgroundColor: i === current ? '#D4AF37' : '#D1D5DB' }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox imagen */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={t('home.reviewPhotoAlt')}
        >
          <img
            src={lightbox}
            alt={t('home.reviewPhotoAlt')}
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center text-2xl"
            aria-label={t('home.reviewClose')}
            title={t('home.reviewClose')}
          >×</button>
        </div>
      )}
    </section>
  );
}

function SobreNosotros() {
  const { t } = useTranslation();
  return (
    <section id="sobre-nosotros" className="py-24 bg-white relative overflow-hidden">
      <div className="container mx-auto px-6 relative">
        <div className="text-center mb-14">
          <div className="divider-ornament max-w-xs mx-auto mb-4">✦ ✦ ✦</div>
          <h2 className="text-4xl md:text-5xl mt-3" style={{ color: 'var(--page-heading)' }}>{t('about.storyTitle')}</h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
          {/* Texto */}
          <div className="space-y-6">
            <p className="text-lg text-[#6B7280] leading-relaxed">
              {t('about.p1')}
            </p>
            <p className="text-[#6B7280] leading-relaxed">
              {t('about.p2')}
            </p>
            <p className="text-[#6B7280] leading-relaxed">
              {t('about.p3')}
            </p>
            <div className="flex gap-8 pt-4">
              <div>
                <div className="text-3xl font-bold text-gradient-gold">500+</div>
                <div className="text-sm text-[#6B7280] mt-1">{t('hero.statFigures')}</div>
              </div>
              <div className="w-px bg-[#D4AF37]/20" />
              <div>
                <div className="text-3xl font-bold text-gradient-gold">5</div>
                <div className="text-sm text-[#6B7280] mt-1">{t('about.statYears')}</div>
              </div>
              <div className="w-px bg-[#D4AF37]/20" />
              <div>
                <div className="text-3xl font-bold text-gradient-gold">4.9★</div>
                <div className="text-sm text-[#6B7280] mt-1">{t('hero.statRating')}</div>
              </div>
            </div>
          </div>

          {/* Tarjeta decorativa */}
          <div className="relative">
            <div className="card-boutique p-8" style={{ backgroundColor: 'var(--page-bg, #FAF8F5)' }}>
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37]/15 to-[#D4AF37]/5 flex items-center justify-center mb-6 halo-gold">
                <span className="text-2xl">💛</span>
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--page-heading)' }}>
                {t('about.uniqueTitle')}
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed">
                {t('about.uniqueDesc')}
              </p>
              <div className="mt-6 pt-6 border-t border-[#D4AF37]/15 flex items-center gap-3">
                <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                <span className="text-sm text-[#6B7280]">{t('about.address')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Suscribete() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consentEmail, setConsentEmail] = useState(false);
  const [consentPhone, setConsentPhone] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [showLegal, setShowLegal] = useState<'privacidad' | null>(null);
  const [newsletterConfig, setNewsletterConfig] = useState<{ imageUrl: string | null; title: string; subtitle: string }>({
    imageUrl: null,
    title: t('newsletter.title'),
    subtitle: t('newsletter.subtitle'),
  });

  useEffect(() => {
    fetch('/api/site/newsletter').then(r => r.json()).then(setNewsletterConfig).catch(() => {});
  }, []);

  const canSubmit = ((email && consentEmail) || (phone && consentPhone)) && status !== 'sending';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('sending');
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email || undefined, phone: phone || undefined, consentEmail, consentPhone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error');
      setStatus('success');
      setMessage(data.message);
    } catch (err: any) {
      setStatus('error');
      setMessage(err.message || 'Error al suscribir');
    }
  };

  return (
    <section id="suscribete" className="py-24 bg-gradient-to-b from-[#1E3A5F] to-[#162d4a]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-14">
          <span className="text-sm text-[#D4AF37] font-medium tracking-wider uppercase">{t('home.newsletterTag')}</span>
          <h2 className="text-4xl md:text-5xl mt-3 text-white">{newsletterConfig.title}</h2>
          <p className="text-white/60 mt-4 max-w-lg mx-auto">{newsletterConfig.subtitle}</p>
        </div>

        <div className="max-w-lg mx-auto w-full space-y-8">
          {/* Imagen configurable arriba */}
          {newsletterConfig.imageUrl && (
            <div className="flex justify-center">
              <img
                src={newsletterConfig.imageUrl}
                alt={t('home.newsletterImageAlt')}
                className="rounded-3xl shadow-elegant max-h-[280px] object-contain"
              />
            </div>
          )}

          {/* Formulario */}
          <div className="w-full">
            {status === 'success' ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-2xl font-semibold mb-2 text-white">{t('home.newsletterThanksTitle')}</h3>
                <p className="text-white/70">{message}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                <div className="rounded-3xl p-8 border border-[#D4AF37]/20 shadow-elegant bg-white">
                  {/* Email */}
                  <div className="mb-5">
                    <label htmlFor="newsletter-email" className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: 'var(--page-heading)' }}>
                      <Mail className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" /> {t('home.newsletterEmail')}
                    </label>
                    <input
                      id="newsletter-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder={t('home.newsletterEmailPh')}
                      className="w-full px-4 py-3 rounded-xl border border-[#D4AF37]/20 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 outline-none transition-colors text-sm"
                    />
                  </div>

                  {/* Teléfono */}
                  <div className="mb-6">
                    <label htmlFor="newsletter-phone" className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: 'var(--page-heading)' }}>
                      <Phone className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" /> {t('home.newsletterPhone')}
                    </label>
                    <input
                      id="newsletter-phone"
                      type="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder={t('home.newsletterPhonePh')}
                      className="w-full px-4 py-3 rounded-xl border border-[#D4AF37]/20 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 outline-none transition-colors text-sm"
                    />
                  </div>

                  {/* Consentimientos */}
                  <div className="space-y-3 mb-6">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={consentEmail}
                        onChange={e => setConsentEmail(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-[#D4AF37]/40 text-[#D4AF37] focus:ring-[#D4AF37]/30 accent-[#D4AF37]"
                      />
                      <span className="text-xs text-[#6B7280] leading-relaxed">
                        <Trans i18nKey="home.newsletterConsentEmail" components={{ strong: <strong /> }} />
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={consentPhone}
                        onChange={e => setConsentPhone(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-[#D4AF37]/40 text-[#D4AF37] focus:ring-[#D4AF37]/30 accent-[#D4AF37]"
                      />
                      <span className="text-xs text-[#6B7280] leading-relaxed">
                        <Trans i18nKey="home.newsletterConsentPhone" components={{ strong: <strong /> }} />
                      </span>
                    </label>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="w-full py-3 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed btn-nacre"
                  >
                    <Send className="w-4 h-4" />
                    {status === 'sending' ? t('home.newsletterSending') : t('home.newsletterSubmit')}
                  </button>

                  {status === 'error' && (
                    <p role="alert" className="text-red-500 text-xs text-center mt-3">{message}</p>
                  )}

                  {/* Legal */}
                  <p className="text-[11px] text-[#4B5563] text-center mt-4 leading-relaxed">
                    <Trans
                      i18nKey="home.newsletterLegal"
                      components={{
                        privacyLink: <button type="button" onClick={() => setShowLegal('privacidad')} className="underline hover:text-[#B8941F] text-[#7A5F0A] transition-colors" />,
                      }}
                    />
                  </p>
                  <p className="text-[11px] text-[#6B7280] text-center mt-2 leading-relaxed">
                    <Trans
                      i18nKey="home.newsletterRecaptcha"
                      components={{
                        privacyLink: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#B8941F] text-[#7A5F0A] transition-colors" />,
                        termsLink: <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#B8941F] text-[#7A5F0A] transition-colors" />,
                      }}
                    />
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {showLegal && (
        <Suspense fallback={null}>
          <LegalModal tipo={showLegal} onClose={() => setShowLegal(null)} />
        </Suspense>
      )}
    </section>
  );
}

function Footer({ logoUrl, socialLinks, onWhatsAppClick }: { logoUrl?: string | null; socialLinks?: { whatsapp: string; instagram: string; facebook: string; tiktok: string }; onWhatsAppClick?: () => void }) {
  const { t } = useTranslation();
  const [showLegal, setShowLegal] = useState<'privacidad' | 'terminos' | 'cookies' | null>(null);
  const mapsUrl = 'https://maps.google.com/?q=Calle+Caballeros+8+13001+Ciudad+Real+España';

  return (
    <footer className="text-white" style={{ backgroundColor: 'var(--page-footer, #1E3A5F)' }}>

      {/* ── Logo hero ── */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-6 py-6 flex flex-col items-center text-center">
          {logoUrl ? (
            isVideoUrl(logoUrl)
              ? <video src={logoUrl} autoPlay muted loop playsInline className="w-72 h-72 object-contain mb-3" />
              : <img src={logoUrl} alt={t('home.footerLogoAlt')} className="w-72 h-72 object-contain mb-3" />
          ) : (
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center mb-3">
              <Sparkles className="w-12 h-12 text-white" />
            </div>
          )}
          <p className="text-lg font-light tracking-wide text-white/85 max-w-xs leading-relaxed"
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            {t('home.footerTaglineLine1')}<br />{t('home.footerTaglineLine2')}
          </p>
        </div>
      </div>

      {/* ── 3 columnas ── */}
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 py-10 border-b border-white/10">

          {/* Col 1 — Navegación */}
          <div>
            <p className="font-bold text-xs tracking-[0.15em] uppercase text-[#F4E4A6] mb-5">{t('home.footerNav')}</p>
            <ul className="space-y-3">
              <li><a href="#como-funciona" className="text-sm text-white/65 hover:text-white transition-colors">{t('home.footerHowItWorks')}</a></li>
              <li><a href="#precios" className="text-sm text-white/65 hover:text-white transition-colors">{t('home.footerPricing')}</a></li>
              <li><a href="#sobre-nosotros" className="text-sm text-white/65 hover:text-white transition-colors">{t('home.footerAbout')}</a></li>
            </ul>
          </div>

          {/* Col — Landings SEO: enlaces internos rastreables a cada página */}
          <nav aria-label={t('home.footerIdeas')}>
            <p className="font-bold text-xs tracking-[0.15em] uppercase text-[#F4E4A6] mb-5">{t('home.footerIdeas')}</p>
            <ul className="space-y-3">
              {LANDINGS.map(l => (
                <li key={l.slug}><a href={`/${l.slug}`} className="text-sm text-white/65 hover:text-white transition-colors">{l.nav}</a></li>
              ))}
            </ul>
          </nav>

          {/* Col 2 — Contacto */}
          <div>
            <p className="font-bold text-xs tracking-[0.15em] uppercase text-[#F4E4A6] mb-5">{t('home.footerContact')}</p>
            <ul className="space-y-4">
              <li>
                <a href="mailto:pedidos@funcomunion.es" className="flex items-start gap-3 text-sm text-white/65 hover:text-white transition-colors">
                  <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#D4AF37]" />
                  <span>pedidos@funcomunion.es</span>
                </a>
              </li>
              <li>
                <a href="https://wa.me/34627602815" target="_blank" rel="noopener noreferrer" onClick={() => onWhatsAppClick?.()} className="flex items-start gap-3 text-sm text-white/65 hover:text-white transition-colors">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#D4AF37]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>+34 627 602 815</span>
                </a>
              </li>
              <li>
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-sm text-white/65 hover:text-white transition-colors">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#D4AF37]" />
                  <span className="leading-relaxed">{t('home.footerAddress1')}<br />{t('home.footerAddress2')}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Col 3 — Redes */}
          <div>
            <p className="font-bold text-xs tracking-[0.15em] uppercase text-[#F4E4A6] mb-5">{t('home.footerFollow')}</p>
            <div className="space-y-3">
              {socialLinks?.instagram && (
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-white/65 hover:text-white transition-colors group">
                  <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-[#E1306C] transition-colors flex-shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </span>
                  Instagram
                </a>
              )}
              {socialLinks?.facebook && (
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-white/65 hover:text-white transition-colors group">
                  <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-[#1877F2] transition-colors flex-shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </span>
                  Facebook
                </a>
              )}
              {socialLinks?.tiktok && (
                <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-white/65 hover:text-white transition-colors group">
                  <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-black transition-colors flex-shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                  </span>
                  TikTok
                </a>
              )}
              {!socialLinks?.instagram && !socialLinks?.facebook && !socialLinks?.tiktok && (
                <p className="text-sm text-white/70 italic">{t('home.footerNoSocial')}</p>
              )}
            </div>
          </div>
        </div>

        {/* ── Barra inferior ── */}
        <div className="pt-5 pb-20 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-4 flex-wrap justify-center sm:justify-start">
            <button type="button" onClick={() => setShowLegal('privacidad')} className="text-xs text-white/75 hover:text-white transition-colors underline-offset-2 hover:underline">{t('footer.privacy')}</button>
            <span className="text-white/60" aria-hidden="true">·</span>
            <button type="button" onClick={() => setShowLegal('terminos')} className="text-xs text-white/75 hover:text-white transition-colors underline-offset-2 hover:underline">{t('footer.terms')}</button>
            <span className="text-white/60" aria-hidden="true">·</span>
            <button type="button" onClick={() => setShowLegal('cookies')} className="text-xs text-white/75 hover:text-white transition-colors underline-offset-2 hover:underline">{t('footer.cookies')}</button>
            <span className="text-white/60" aria-hidden="true">·</span>
            <a href="/accesibilidad" className="text-xs text-white/75 hover:text-white transition-colors underline-offset-2 hover:underline">{t('footer.accessibility')}</a>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 text-xs text-white/70">
            <span>{t('home.footerCopy')}</span>
            <span className="hidden sm:inline text-white/40" aria-hidden="true">·</span>
            <span>{t('home.footerHandmade')}</span>
          </div>
        </div>
      </div>

      <Suspense fallback={null}>
        {showLegal && <LegalModal tipo={showLegal} onClose={() => setShowLegal(null)} />}
      </Suspense>
    </footer>
  );
}

// Marca una notif como leida en backend (idempotente, no falla si no existe).
function markNotifRead(userId: string | undefined, notifId: string) {
  if (!userId || !notifId) return;
  fetch(`/api/users/${userId}/notifications/read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids: [notifId] }),
  }).catch(() => {});
}

// ─────────────────────────────────────────────────────────────────────────
// STOCK / OUTLET — piezas de escaparate ya hechas, disponibles ya.
// ─────────────────────────────────────────────────────────────────────────
interface StockPublicItem {
  id: string;
  title: string;
  description: string;
  priceEur: number;
  priceOriginalEur?: number | null;
  imageUrl: string | null;
  extraImages: string[];
  tipo: string | null;
  genero: 'nino' | 'nina' | null;
}

const STOCK_GASTOS_ENVIO = 12;

function StockBuyModal({ item, onClose, onConfirm }: {
  item: StockPublicItem;
  onClose: () => void;
  onConfirm: (metodo: 'recogida' | 'envio', addr: any) => void;
}) {
  const { t } = useTranslation();
  const [metodo, setMetodo] = useState<'recogida' | 'envio'>('recogida');
  const [addr, setAddr] = useState({ name: '', phone: '', street: '', postalCode: '', city: '', province: '' });
  const total = item.priceEur + (metodo === 'envio' ? STOCK_GASTOS_ENVIO : 0);
  const canSubmit = metodo === 'recogida' || (addr.name && addr.phone && addr.street && addr.postalCode && addr.city);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={item.title || t('stock.uniquePiece')}
    >
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-4 border-b">
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
            <StockPhotos item={item} />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-[#1E3A5F] truncate">
              {item.title || t('stock.uniquePiece')}
              {item.tipo && <span className="ml-2 text-[11px] font-medium text-[#B8941F] uppercase tracking-wide">{item.tipo}</span>}
            </p>
            <p className="text-sm font-bold flex items-baseline gap-1.5">
              <span className="text-[#D4AF37]">{item.priceEur.toFixed(2)}€</span>
              {item.priceOriginalEur != null && item.priceOriginalEur > item.priceEur && (
                <span className="text-xs text-gray-400 line-through font-normal">{item.priceOriginalEur.toFixed(2)}€</span>
              )}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className="ml-auto text-gray-400 hover:text-gray-600"><X className="w-5 h-5" aria-hidden="true" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <p className="text-sm font-medium text-[#1E3A5F] mb-2">{t('stock.deliveryMethod')}</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setMetodo('recogida')}
                className={`p-3 rounded-lg border text-sm ${metodo === 'recogida' ? 'border-[#D4AF37] bg-[#FBF8EF] font-medium' : 'border-gray-200'}`}>
                🏪 {t('stock.pickup')}
              </button>
              <button onClick={() => setMetodo('envio')}
                className={`p-3 rounded-lg border text-sm ${metodo === 'envio' ? 'border-[#D4AF37] bg-[#FBF8EF] font-medium' : 'border-gray-200'}`}>
                📦 {t('stock.shipping')} +{STOCK_GASTOS_ENVIO}€
              </button>
            </div>
          </div>
          {metodo === 'envio' && (
            <div className="space-y-2">
              {([['name', t('stock.fName')], ['phone', t('stock.fPhone')], ['street', t('stock.fStreet')], ['postalCode', t('stock.fPostal')], ['city', t('stock.fCity')], ['province', t('stock.fProvince')]] as const).map(([k, label]) => (
                <input key={k} type="text" placeholder={label}
                  aria-label={label}
                  value={(addr as any)[k]}
                  onChange={e => setAddr(a => ({ ...a, [k]: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              ))}
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-sm text-[#6B7280]">{t('stock.total')}</span>
            <span className="text-xl font-bold text-[#1E3A5F]">{total.toFixed(2)}€</span>
          </div>
          <button onClick={() => onConfirm(metodo, metodo === 'envio' ? addr : null)}
            disabled={!canSubmit}
            className="w-full py-3 rounded-full bg-[#D4AF37] hover:bg-[#B8941F] disabled:opacity-50 text-white font-medium">
            {t('stock.confirmBuy')}
          </button>
          <p className="text-xs text-center text-[#9CA3AF]">{t('stock.payNote')}</p>
        </div>
      </div>
    </div>
  );
}

// Mini-carrusel de fotos para una pieza de stock (foto principal + extras).
function StockPhotos({ item, rounded }: { item: StockPublicItem; rounded?: string }) {
  const photos = [item.imageUrl, ...(item.extraImages || [])].filter(Boolean) as string[];
  const [idx, setIdx] = useState(0);
  if (photos.length === 0) return <div className={`w-full h-full bg-[#FAF8F5] ${rounded || ''}`} />;
  const safeIdx = Math.min(idx, photos.length - 1);
  return (
    <div className={`relative w-full h-full ${rounded || ''} overflow-hidden`}>
      {/* Las fotos del stock las sube el admin desde el móvil y pesan lo suyo.
          lazy + async evita que bloqueen el pintado de la portada; el fondo
          crema deja un hueco con color en vez de un recuadro roto mientras
          llegan. La compresión de verdad se hace al subirlas (lib/imagen). */}
      <img
        src={photos[safeIdx]}
        alt={item.title || 'Pieza'}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover bg-[#FAF8F5]"
      />
      {photos.length > 1 && (
        <>
          <button type="button" onClick={(e) => { e.stopPropagation(); setIdx((safeIdx - 1 + photos.length) % photos.length); }}
            aria-label="Foto anterior"
            className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow">
            <ChevronLeft className="w-4 h-4 text-[#1E3A5F]" aria-hidden="true" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); setIdx((safeIdx + 1) % photos.length); }}
            aria-label="Siguiente foto"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow">
            <ChevronRight className="w-4 h-4 text-[#1E3A5F]" aria-hidden="true" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {photos.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === safeIdx ? 'bg-white' : 'bg-white/50'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StockDisponible({ items, onBuy }: { items: StockPublicItem[]; onBuy: (item: StockPublicItem, metodo: 'recogida' | 'envio', addr: any) => void }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<StockPublicItem | null>(null);

  if (items.length === 0) return null;

  return (
    <section id="disponibles" className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#D4AF37]/10 text-[#866A12] text-sm font-medium mb-3">
            ✨ {t('stock.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('stock.title')}
          </h2>
          <p className="text-[#6B7280] mt-2 max-w-xl mx-auto">{t('stock.subtitle')}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(it => (
            <div key={it.id} className="rounded-2xl border border-[#D4AF37]/25 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="relative aspect-square bg-[#FAF8F5]">
                <StockPhotos item={it} />
                <span className="absolute top-3 left-3 text-[11px] font-medium px-2.5 py-1 rounded-full bg-white/90 text-[#B8941F] shadow-sm pointer-events-none">
                  {t('stock.uniquePiece')}
                </span>
                {it.priceOriginalEur != null && it.priceOriginalEur > it.priceEur && (
                  <span className="absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full bg-rose-600 text-white shadow pointer-events-none">
                    -{Math.round((1 - it.priceEur / it.priceOriginalEur) * 100)}%
                  </span>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-[#1E3A5F]">{it.title || t('stock.uniquePiece')}</p>
                  {it.tipo && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#866A12] uppercase tracking-wide">
                      {it.tipo}
                    </span>
                  )}
                </div>
                {it.description && <p className="text-sm text-[#6B7280] mt-1 line-clamp-2">{it.description}</p>}
                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[#9A7A16]">{it.priceEur.toFixed(2)}€</span>
                    {it.priceOriginalEur != null && it.priceOriginalEur > it.priceEur && (
                      <span className="text-sm text-gray-400 line-through">{it.priceOriginalEur.toFixed(2)}€</span>
                    )}
                  </div>
                  <button onClick={() => setSelected(it)}
                    className="px-5 py-2 rounded-full bg-[#1E3A5F] hover:bg-[#162d4a] text-white text-sm font-medium">
                    {t('stock.iWantIt')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {selected && (
        <StockBuyModal item={selected} onClose={() => setSelected(null)}
          onConfirm={(metodo, addr) => { onBuy(selected, metodo, addr); setSelected(null); }} />
      )}
    </section>
  );
}

export default function Home() {
  const { t } = useTranslation();
  // Sincroniza <html lang="..."> con el idioma activo (WCAG 3.1.1).
  useHtmlLang();
  const [mostrarConfigurador, setMostrarConfigurador] = useState(false);
  // Mensaje de salida del configurador (despedida / oferta / recordatorio).
  const [exitModal, setExitModal] = useState<ExitModalKind | null>(null);
  // Llegada por el enlace de recomendación de otro cliente (?ref=CÓDIGO).
  useEffect(() => { capturarReferido(); }, []);
  const lanzarMensajeSalida = (motivo: 'cerrar' | 'raton') => {
    if (!hasDraftInProgress()) return false;
    const kind = decideExitModal();
    if (!kind) return false;
    markExitModalShown(kind);
    setExitModal(kind);
    trackEvento('exit_modal', { kind, motivo });
    return true;
  };
  const cerrarConfigurador = () => {
    setMostrarConfigurador(false);
    setInitialTipo(undefined);
    lanzarMensajeSalida('cerrar');
  };
  // Escritorio: el ratón sale por arriba de la ventana (hacia pestañas o la X
  // del navegador) con el configurador abierto. Una vez por visita.
  useEffect(() => {
    if (!mostrarConfigurador) return;
    const onOut = (e: MouseEvent) => {
      if (e.relatedTarget || e.clientY > 0) return;
      try { if (sessionStorage.getItem('funcomunion_exit_mouse')) return; } catch { /* */ }
      if (lanzarMensajeSalida('raton')) {
        try { sessionStorage.setItem('funcomunion_exit_mouse', '1'); } catch { /* */ }
      }
    };
    document.addEventListener('mouseout', onOut);
    return () => document.removeEventListener('mouseout', onOut);
  }, [mostrarConfigurador]); // eslint-disable-line react-hooks/exhaustive-deps
  const [draftKey, setDraftKey] = useState(0);
  const [mostrarAuth, setMostrarAuth] = useState(false);
  const [mostrarDashboard, setMostrarDashboard] = useState(false);
  const [dashboardTab, setDashboardTab] = useState<string | undefined>(undefined);
  const [dashboardOrderId, setDashboardOrderId] = useState<string | undefined>(undefined);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [pendingConfig, setPendingConfig] = useState<FiguraConfig | null>(null);
  const [initialTipo, setInitialTipo] = useState<string | undefined>(undefined);
  const [adminAlerts, setAdminAlerts] = useState(() => localStorage.getItem('funcomunion_admin_alerts') !== 'off');
  const anonAlertSent = useRef(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Restore cached logos/social to avoid flash on reload
  const cachedSite = (() => { try { return JSON.parse(localStorage.getItem('funcomunion_site_config') || '{}'); } catch { return {}; } })();
  const [logoNinoUrl, setLogoNinoUrl] = useState<string | null>(cachedSite.logoNinoUrl || null);
  const [logoTextoUrl, setLogoTextoUrl] = useState<string | null>(cachedSite.logoTextoUrl || null);
  const [logoNinaUrl, setLogoNinaUrl] = useState<string | null>(cachedSite.logoNinaUrl || null);
  const [footerLogoNinoUrl, setFooterLogoNinoUrl] = useState<string | null>(cachedSite.footerLogoNinoUrl || null);
  const [footerLogoNinaUrl, setFooterLogoNinaUrl] = useState<string | null>(cachedSite.footerLogoNinaUrl || null);
  const [socialLinks, setSocialLinks] = useState(cachedSite.socialLinks || { whatsapp: '', instagram: '', facebook: '', tiktok: '' });
  // Stock / outlet: fetch único aquí para saber si mostrar el enlace en el menú
  // y alimentar la sección sin doble petición.
  const [stockItems, setStockItems] = useState<StockPublicItem[]>([]);
  useEffect(() => {
    fetch('/api/site/stock')
      .then(r => r.json())
      .then(d => setStockItems(Array.isArray(d?.items) ? d.items : []))
      .catch(() => {});
  }, []);
  const { genero, setGenero } = useGender();
  const logoUrl = genero === 'nina' ? (logoNinaUrl || logoNinoUrl) : (logoNinoUrl || logoNinaUrl);
  const footerLogoUrl = genero === 'nina' ? (footerLogoNinaUrl || footerLogoNinoUrl || logoUrl) : (footerLogoNinoUrl || footerLogoNinaUrl || logoUrl);

  // Dynamic favicon: prefer logoTexto, fallback to logo
  useEffect(() => {
    const faviconSrc = logoTextoUrl || logoUrl;
    if (!faviconSrc) return;
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (link) link.href = faviconSrc;
    const apple = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
    if (apple) apple.href = faviconSrc;
  }, [logoTextoUrl, logoUrl]);

  // Deep-link from emails: ?dashboard=1&order=ID — abre el dashboard al pedido especifico.
  // Si no esta logueado todavia, espera al login (mantiene los params en estado para reabrir).
  const [pendingDashboardLink, setPendingDashboardLink] = useState<{ tab?: string; orderId?: string } | null>(() => {
    if (typeof window === 'undefined') return null;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('dashboard') !== '1') return null;
    return { tab: sp.get('tab') || undefined, orderId: sp.get('order') || undefined };
  });
  useEffect(() => {
    if (!pendingDashboardLink) return;
    if (!isLoggedIn) { setMostrarAuth(true); return; }
    if (pendingDashboardLink.tab) setDashboardTab(pendingDashboardLink.tab);
    if (pendingDashboardLink.orderId) setDashboardOrderId(pendingDashboardLink.orderId);
    setMostrarDashboard(true);
    setPendingDashboardLink(null);
    // limpiar URL para que un refresh no reabra
    const url = new URL(window.location.href);
    url.searchParams.delete('dashboard');
    url.searchParams.delete('order');
    url.searchParams.delete('tab');
    window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
  }, [isLoggedIn, pendingDashboardLink]);

  const PENDING_KEY = 'funcomunion_pending_order';
  // El pedido pendiente sobrevive a la recarga del login con Google en móvil.
  // Antes se guardaba entero (con fotos en base64) y, si no cabía, el error
  // se ignoraba: el pedido se perdía sin rastro. Ahora se intenta entero, luego
  // sin las fotos grandes (siguen en la sesión temporal) y, si no, en sessionStorage.
  const guardarPendiente = (config: FiguraConfig) => {
    const sinFotos = (c: any) => {
      const big = (v: unknown) => typeof v === 'string' && v.startsWith('data:') && v.length > 200000;
      const out: any = { ...c };
      if (big(out.fotoReferencia)) out.fotoReferencia = undefined;
      if (big(out.previewImage)) out.previewImage = undefined;
      if (Array.isArray(out.refPhotoHistory)) out.refPhotoHistory = out.refPhotoHistory.filter((x: unknown) => !big(x));
      if (out.configSegundaFigura && big(out.configSegundaFigura.fotoReferencia)) out.configSegundaFigura = { ...out.configSegundaFigura, fotoReferencia: undefined };
      return out;
    };
    for (const intento of [config, sinFotos(config)]) {
      for (const store of [localStorage, sessionStorage]) {
        try { store.setItem(PENDING_KEY, JSON.stringify({ ...intento, _pendienteAt: Date.now() })); return true; } catch { /* sin sitio: siguiente */ }
      }
    }
    track('pedido_error', { via: 'guardar_pendiente', motivo: 'sin espacio' });
    return false;
  };
  const borrarPendiente = () => {
    try { localStorage.removeItem(PENDING_KEY); } catch { /* */ }
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* */ }
  };
  // Pedido creado: fuera el pendiente y el borrador del configurador (antes el
  // borrador se borraba ANTES de crear el pedido y un fallo lo perdía todo).
  const pedidoCreadoOk = (config: FiguraConfig, via: string) => {
    borrarPendiente();
    try { localStorage.removeItem('funcomunion_wizard_draft'); } catch { /* */ }
    clearTempSession();
    track('pedido_creado', { tipo: config.tipo, total: config.total, envio: config.metodoEnvio, via });
  };
  // Fallo al crear: queda registrado, el cliente lo ve en su idioma y su
  // figura sigue guardada para reintentar.
  const pedidoFallido = (config: FiguraConfig, err: any, via: string) => {
    const msg = String(err?.message || err || 'error');
    track('pedido_error', { via, motivo: msg.slice(0, 120), tipo: config.tipo, total: config.total });
    setPendingConfig(config);
    alert(t('home.orderErrorRetry', { msg }));
  };

  // Track helper
  const track = (event: string, data?: any) => {
    trackEvento(event, { ...data, ...(currentUser?.nombre ? { nombre: currentUser.nombre } : {}) });
  };

  // Ejecutar limpieza de sesiones al cargar
  useEffect(() => {
    scheduleCleanup();
    const entrada = Date.now();
    // Track page view
    trackEvento('page_view');
    // Track page exit
    const handleExit = () => {
      const reason = document.visibilityState === 'hidden' ? 'tab_hidden' : 'page_unload';
      trackBeacon('exit', { reason, segundos: Math.round((Date.now() - entrada) / 1000) });
    };
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') handleExit(); });
    window.addEventListener('beforeunload', handleExit);

    const savedUser = localStorage.getItem('funcomunion_user');
    let restoredUser: any = null;
    if (savedUser) {
      restoredUser = JSON.parse(savedUser);
      setCurrentUser(restoredUser);
      setIsLoggedIn(true);
      // Send heartbeat for online status
      const hbId = restoredUser?.uid || restoredUser?.id;
      if (hbId) {
        // Refresh user data from API to get latest role/fields
        fetch(`/api/users/${hbId}`)
          .then(r => r.ok ? r.json() : null)
          .then(fresh => {
            if (fresh && fresh.id) {
              const merged = { ...restoredUser, ...fresh };
              setCurrentUser(merged);
              localStorage.setItem('funcomunion_user', JSON.stringify(merged));
              // Si el usuario tiene idioma preferido guardado y es distinto
              // al actual, aplicarlo.
              if (fresh.lang && fresh.lang !== i18n.language?.split('-')[0]) {
                i18n.changeLanguage(fresh.lang);
              }
            }
          })
          .catch(() => {});
        fetch(`/api/users/${hbId}/heartbeat`, { method: 'POST' }).catch(() => {});
        // Register push token (asks permission once)
        requestPushToken(hbId).catch(() => {});
        // Fetch unread notifications and show as toasts
        fetch(`/api/users/${hbId}/notifications`)
          .then(r => r.ok ? r.json() : [])
          .then((notifs: any[]) => {
            const unread = notifs.filter((n: any) => !n.read);
            if (unread.length > 0) {
              setUnreadCount(unread.length);
              // Show latest 3 as toasts. Marca leida al hacer clic o al cerrar.
              unread.slice(0, 3).forEach((n: any, i: number) => {
                setTimeout(() => {
                  toast(n.title || 'Funcomunion', {
                    description: n.message,
                    duration: 6000,
                    icon: '🔔',
                    onAutoClose: () => markNotifRead(hbId, n.id),
                    onDismiss: () => markNotifRead(hbId, n.id),
                    action: { label: 'Ver', onClick: () => markNotifRead(hbId, n.id) },
                  });
                }, i * 800);
              });
            }
          })
          .catch(() => {});
      }
    }

    // Listen for foreground push messages
    onPushMessage((payload: any) => {
      const { title, body } = payload.notification || {};
      if (title) { toast(title, { description: body, duration: 8000, icon: '🔔' }); setUnreadCount(c => c + 1); }
    });

    // Poll for new notifications every 30s as push fallback
    const notifPoll = setInterval(() => {
      const u = (() => { try { return JSON.parse(localStorage.getItem('funcomunion_user') || 'null'); } catch { return null; } })();
      const uid = u?.id || u?.uid;
      if (!uid) return;
      fetch(`/api/users/${uid}/notifications`)
        .then(r => r.ok ? r.json() : [])
        .then((notifs: any[]) => {
          const unread = notifs.filter((n: any) => !n.read);
          const prevCount = parseInt(sessionStorage.getItem('funcomunion_notif_count') || '0', 10);
          if (unread.length > prevCount) {
            // New notifications arrived
            const newOnes = unread.slice(0, unread.length - prevCount);
            newOnes.forEach((n: any, i: number) => {
              setTimeout(() => toast(n.title || 'Funcomunion', {
                description: n.message,
                duration: 6000,
                icon: '🔔',
                onAutoClose: () => markNotifRead(uid, n.id),
                onDismiss: () => markNotifRead(uid, n.id),
                action: { label: 'Ver', onClick: () => markNotifRead(uid, n.id) },
              }), i * 500);
            });
          }
          setUnreadCount(unread.length);
          sessionStorage.setItem('funcomunion_notif_count', String(unread.length));
        })
        .catch(() => {});
    }, 30000);

    // Restore wizard draft (user was configuring but didn't finish)
    const wizardDraft = localStorage.getItem('funcomunion_wizard_draft');
    if (wizardDraft) {
      try {
        const draft = JSON.parse(wizardDraft);
        // Only restore if draft is recent (< 30 days) and has some config.
        // Si hay un pedido pendiente de crear (vuelta del login), no se reabre
        // el configurador: el borrador solo se borra cuando el pedido existe.
        const hayPendiente = !!(localStorage.getItem(PENDING_KEY) || sessionStorage.getItem(PENDING_KEY));
        if (!hayPendiente && draft._savedAt && Date.now() - draft._savedAt < DRAFT_MAX_AGE_MS && draft.config?.tipo) {
          setMostrarConfigurador(true);
        }
      } catch {}
    }

    // Restore pending order config that survived a page refresh
    // Check both localStorage and sessionStorage for pending order
    const savedPending = localStorage.getItem(PENDING_KEY) || sessionStorage.getItem(PENDING_KEY);
    if (savedPending) {
      try {
        const { _pendienteAt, ...config } = JSON.parse(savedPending) as FiguraConfig & { _pendienteAt?: number };
        // Un pendiente de hace más de 7 días ya no se crea solo (evita pedidos
        // fantasma o duplicados semanas después).
        if (_pendienteAt && Date.now() - _pendienteAt > 7 * 24 * 3600_000) {
          borrarPendiente();
        } else if (restoredUser?.id) {
          // Ya ha iniciado sesión (vuelta del login con Google): crear el pedido
          createOrder(config as FiguraConfig, restoredUser.id)
            .then(order => {
              pedidoCreadoOk(config as FiguraConfig, 'restore');
              alert(t("home.orderCreated", { id: order.id.slice(-8), total: config.total?.toFixed(2) }));
              setMostrarDashboard(true);
            })
            .catch(err => pedidoFallido(config as FiguraConfig, err, 'restore'));
        } else {
          // Not logged in — restore and show auth modal
          setPendingConfig(config as FiguraConfig);
          setMostrarAuth(true);
        }
      } catch {
        borrarPendiente();
      }
    }

    fetch('/api/site/logo')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        if (d.logoNinoUrl) setLogoNinoUrl(d.logoNinoUrl);
        if (d.logoNinaUrl) setLogoNinaUrl(d.logoNinaUrl);
        if (d.logoTextoUrl) setLogoTextoUrl(d.logoTextoUrl);
        if (d.footerLogoNinoUrl) setFooterLogoNinoUrl(d.footerLogoNinoUrl);
        if (d.footerLogoNinaUrl) setFooterLogoNinaUrl(d.footerLogoNinaUrl);
        const sl = {
          whatsapp:  d.socialWhatsApp  || '',
          instagram: d.socialInstagram || '',
          facebook:  d.socialFacebook  || '',
          tiktok:    d.socialTikTok    || '',
        };
        setSocialLinks(sl);
        // Cache for instant restore on next load
        try {
          localStorage.setItem('funcomunion_site_config', JSON.stringify({
            logoNinoUrl: d.logoNinoUrl, logoNinaUrl: d.logoNinaUrl, logoTextoUrl: d.logoTextoUrl,
            footerLogoNinoUrl: d.footerLogoNinoUrl, footerLogoNinaUrl: d.footerLogoNinaUrl,
            socialLinks: sl,
          }));
        } catch {}
      })
      .catch(() => {});

    return () => clearInterval(notifPoll);
  }, []);

  const createOrder = async (config: FiguraConfig, userId: string) => {
    // Strip large base64 data before sending (reduces payload from MBs to KBs)
    const cleanConfig = { ...config };
    if (cleanConfig.fotoReferencia?.startsWith('data:') && cleanConfig.fotoReferencia.length > 500000) {
      cleanConfig.fotoReferencia = undefined;
    }
    if (cleanConfig.previewImage?.startsWith('data:') && cleanConfig.previewImage.length > 500000) {
      cleanConfig.previewImage = undefined;
    }
    // Extraer direccion de envio del config y mandarla en el campo top-level
    // del Order (separado de figuraConfig). Sanitizar trims.
    const addrIn = config.shippingAddress;
    const shippingAddress = config.metodoEnvio === 'envio' && addrIn ? {
      name: (addrIn.name || '').trim(),
      phone: (addrIn.phone || '').trim(),
      street: (addrIn.street || '').trim(),
      postalCode: (addrIn.postalCode || '').trim(),
      city: (addrIn.city || '').trim(),
      province: (addrIn.province || '').trim(),
      notes: (addrIn.notes || '').trim() || undefined,
      method: 'envio',
    } : { method: 'recogida' };
    delete cleanConfig.shippingAddress;
    // Adjuntar token Firebase si el usuario esta logueado (anti-IDOR en /orders/create)
    const createHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const { getIdToken } = await import('@/services/auth');
      const tk = await getIdToken();
      if (tk) createHeaders.Authorization = `Bearer ${tk}`;
    } catch (_) {}
    const res = await fetch('/api/orders/create', {
      method: 'POST',
      headers: createHeaders,
      body: JSON.stringify({
        userId,
        // Id anónimo del visitante: el servidor cierra también los borradores
        // que dejó antes de iniciar sesión.
        anonId: (() => { try { return localStorage.getItem('funcomunion_anon_id') || undefined; } catch { return undefined; } })(),
        figuraConfig: cleanConfig,
        totalPrice: config.total,
        shippingAddress,
      })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Error al crear el pedido');
    }
    return res.json();
  };

  const handleAuthSuccess = async (userId: string) => {
    let user: any = { id: userId };
    try {
      const res = await fetch(`/api/users/${userId}`);
      if (res.ok) user = await res.json();
    } catch (err) {
      console.error('Error fetching user after auth:', err);
    }
    setCurrentUser(user);
    setIsLoggedIn(true);
    localStorage.setItem('funcomunion_user', JSON.stringify(user));
    fetch(`/api/users/${userId}/heartbeat`, { method: 'POST' }).catch(() => {});
    // Por el helper, no suelto: si no, este evento pierde la fuente, el idioma
    // y el dispositivo, y rompe el recorrido del visitante.
    trackEvento('login', { nombre: user.nombre });

    // If there was a pending order config, create it now
    if (pendingConfig) {
      try {
        const order = await createOrder(pendingConfig, user.id);
        setPendingConfig(null);
        pedidoCreadoOk(pendingConfig, 'login');
        alert(t("home.orderCreatedShort", { id: order.id.slice(-8), total: pendingConfig.total.toFixed(2) }));
        setMostrarDashboard(true);
      } catch (err: any) {
        pedidoFallido(pendingConfig, err, 'login');
      }
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem('funcomunion_user');
  };

  const handleOpenConfigurador = (tipo?: string) => {
    if (!isLoggedIn) {
      // Toast for anonymous user
      toast(t('home.anonToast'), {
        description: t('home.anonToastDesc'),
        action: { label: t('home.anonToastAction'), onClick: () => setMostrarAuth(true) },
        duration: 8000,
        icon: '🔑',
      });
      // Alert admin (email + toast)
      if (!anonAlertSent.current) {
        anonAlertSent.current = true;
        let anonId: string | null = null;
        try { anonId = localStorage.getItem('funcomunion_anon_id'); } catch {}
        fetch('/api/alert/anonymous-user', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ step: 'inicio', tipo: tipo || 'sin-elegir', userAgent: navigator.userAgent, anonId }),
        }).catch(() => {});
      }
    }
    // Admin toast
    if (currentUser?.rol === 'admin' && adminAlerts && !isLoggedIn) {
      // This case won't happen (admin is logged in), but for other anonymous users
      // the admin will see toasts via the real-time mechanism below
    }
    track('config_start', { tipo: tipo || 'sin-elegir', loggedIn: isLoggedIn });
    setMostrarConfigurador(true);
  };

  // Abre el configurador con un tipo preelegido, preguntando antes si hay un
  // borrador a medias (tarjetas de precios, sección de bodas y enlaces ?configurar=).
  const abrirConfiguradorCon = (tipo: string) => {
    const hasDraft = localStorage.getItem('funcomunion_wizard_draft');
    if (hasDraft) {
      const resume = confirm(t('home.resumeCartConfirm'));
      if (!resume) { localStorage.removeItem('funcomunion_wizard_draft'); setInitialTipo(tipo); }
    } else {
      setInitialTipo(tipo);
    }
    clearTempSession(); handleOpenConfigurador(tipo);
  };

  // Enlaces desde las landings SEO: /?configurar=Novios[&genero=nina].
  const [deepLinkTipo, setDeepLinkTipo] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const tipo = new URLSearchParams(window.location.search).get('configurar');
    return tipo && ['Mini', 'Classic', 'Premium', 'Pareja', 'Novios', 'Llavero'].includes(tipo) ? tipo : null;
  });
  useEffect(() => {
    if (!deepLinkTipo) return;
    // Con sesión guardada, esperar a que se restaure: si no, el aviso de
    // "usuario anónimo configurando" saltaría también para clientes logueados.
    let haySesion = false;
    try { haySesion = !!localStorage.getItem('funcomunion_user'); } catch { /* sin storage */ }
    if (haySesion && !isLoggedIn) return;
    const url = new URL(window.location.href);
    const g = url.searchParams.get('genero');
    if (g === 'nino' || g === 'nina') setGenero(g);
    url.searchParams.delete('configurar');
    url.searchParams.delete('genero');
    window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    setDeepLinkTipo(null);
    abrirConfiguradorCon(deepLinkTipo);
  }, [deepLinkTipo, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compra de pieza de STOCK: construye un FiguraConfig "ligero" (sin
  // personalización ni IA) y reutiliza el mismo flujo de pedido — incluido
  // el login automático si el cliente no está logueado.
  const handleStockBuy = (item: StockPublicItem, metodo: 'recogida' | 'envio', addr: any) => {
    const total = item.priceEur + (metodo === 'envio' ? 12 : 0);
    const stockConfig: FiguraConfig = {
      tipo: (item.tipo as FiguraConfig['tipo']) || 'Classic',
      genero: item.genero || 'nino',
      nombreNino: item.title || 'Pieza de escaparate',
      fechaComunion: '',
      accesorios: [],
      basePersonalizada: false,
      textoBase: '',
      urgente: false,
      total,
      esStock: true,
      stockItemId: item.id,
      ...(item.imageUrl ? { selectedPreviewUrl: item.imageUrl, previewGallery: [item.imageUrl] } : {}),
      metodoEnvio: metodo,
      gastosEnvio: metodo === 'envio' ? 12 : 0,
      cantidad: 1,
      ...(metodo === 'envio' && addr ? { shippingAddress: { ...addr, method: 'envio' } } : {}),
    };
    handleConfigComplete(stockConfig);
  };

  const handleConfigComplete = async (config: FiguraConfig) => {
    setMostrarConfigurador(false);

    if (!isLoggedIn || !currentUser?.id) {
      // Save config in state AND sessionStorage (survives page refresh)
      setPendingConfig(config);
      guardarPendiente(config);

      // Also save to server as pending_generation (survives browser close)
      // Mismo id de visitante que la analítica y el configurador (localStorage).
      // Antes usaba otro de sessionStorage y el borrador nunca se cerraba al
      // hacer el pedido: seguía saliendo como carrito abandonado.
      const anonId = visitorIdTrack();
      fetch('/api/wizard/save-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: anonId,
          figuraConfig: { ...config, fotoReferencia: undefined, previewImage: undefined },
          totalPrice: config.total,
          previewGallery: config.previewGallery,
          selectedPreviewUrl: config.selectedPreviewUrl,
        }),
      }).catch(() => {}); // fire-and-forget

      // Muro de registro: es el punto donde más gente se puede caer, así que
      // queda registrado para poder medirlo.
      track('login_requerido', { tipo: config.tipo, total: config.total });
      setMostrarAuth(true);
      return;
    }

    // Guardado ANTES de crear: si falla, la figura no se pierde.
    guardarPendiente(config);
    try {
      const order = await createOrder(config, currentUser.id);
      pedidoCreadoOk(config, 'directo');
      alert(t("home.orderCreated", { id: order.id.slice(-8), total: config.total.toFixed(2) }));
      setMostrarDashboard(true);
    } catch (err: any) {
      // No dejamos el pendiente para que no se reintente solo en la próxima
      // carga: el cliente decide (su borrador del configurador sigue guardado).
      borrarPendiente();
      pedidoFallido(config, err, 'directo');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Skip link — accesibilidad (WCAG 2.4.1). Visualmente oculto hasta foco. */}
      <a href="#main-content" className="skip-link">
        {t('accessibility.skipToContent')}
      </a>
      {/* SEO dinámico (título, descripción, og, hreflang). LocalBusiness
          schema ya está inline en index.html para crawler sin JS. */}
      <SEO path="/" titleKey="seo.home.title" descriptionKey="seo.home.description" />
      {/* Product Schema con precios reales + AggregateRating de reviews */}
      <ProductSchema />
      <Toaster position="top-right" richColors closeButton />

      {/* Admin alert toggle — floating pill */}
      {currentUser?.rol === 'admin' && (
        <button
          onClick={() => {
            const next = !adminAlerts;
            setAdminAlerts(next);
            localStorage.setItem('funcomunion_admin_alerts', next ? 'on' : 'off');
            toast(next ? t('home.adminAlertsToggleOn') : t('home.adminAlertsToggleOff'), { duration: 2000 });
          }}
          className={`fixed top-20 right-4 z-[70] flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg transition-all ${
            adminAlerts
              ? 'bg-green-500 text-white hover:bg-green-600'
              : 'bg-gray-400 text-white hover:bg-gray-500'
          }`}
          title={adminAlerts ? t('home.adminAlertsTitleOn') : t('home.adminAlertsTitleOff')}
        >
          {adminAlerts ? t('home.adminAlertsOn') : t('home.adminAlertsOff')}
        </button>
      )}

      <Header
        onAuthClick={() => setMostrarAuth(true)}
        isLoggedIn={isLoggedIn}
        onLogout={handleLogout}
        onDashboardClick={() => setMostrarDashboard(true)}
        isAdmin={currentUser?.rol === 'admin'}
        hasStock={stockItems.length > 0}
        logoUrl={logoUrl}
        logoTextoUrl={logoTextoUrl}
        onCartClick={!mostrarConfigurador ? () => setMostrarConfigurador(true) : undefined}
        onCartDiscard={!mostrarConfigurador ? () => {
          if (confirm(t('home.discardCartConfirm'))) {
            // Save discarded draft to pending_generations before removing
            try {
              const draft = JSON.parse(localStorage.getItem('funcomunion_wizard_draft') || 'null');
              if (draft?.config?.tipo) {
                const uid = currentUser?.id || sessionStorage.getItem('funcomunion_anon_id') || 'anon_discarded';
                fetch('/api/wizard/save-draft', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    userId: uid,
                    figuraConfig: { ...draft.config, fotoReferencia: undefined, previewImage: undefined },
                    totalPrice: null,
                    previewGallery: draft.previewGallery || [],
                    selectedPreviewUrl: draft.selectedPreviewUrl || null,
                  }),
                }).catch(() => {});
                track('exit', { reason: 'draft_discarded', tipo: draft.config.tipo });
              }
            } catch {}
            localStorage.removeItem('funcomunion_wizard_draft');
            setDraftKey(k => k + 1);
          }
        } : undefined}
        cartInfo={!mostrarConfigurador ? (() => {
          try {
            const d = JSON.parse(localStorage.getItem('funcomunion_wizard_draft') || 'null');
            if (!d?.config?.tipo || !d._savedAt || Date.now() - d._savedAt > DRAFT_MAX_AGE_MS) return null;
            return { tipo: d.config.tipo };
          } catch { return null; }
        })() : null}
        unreadCount={unreadCount}
        onNotifClick={() => { setDashboardTab('notificaciones'); setMostrarDashboard(true); }}
      />

      <main id="main-content" tabIndex={-1}>
        <Hero onStart={() => {
          const hasDraft = localStorage.getItem('funcomunion_wizard_draft');
          if (hasDraft) {
            const resume = confirm(t('home.resumeCartConfirm'));
            if (!resume) localStorage.removeItem('funcomunion_wizard_draft');
          }
          clearTempSession(); handleOpenConfigurador();
        }} />
        <ComoFunciona />
        <Precios onSelect={abrirConfiguradorCon} />
        <EscenariosTeaser onDesign={() => abrirConfiguradorCon('Classic')} />
        <BodasTeaser onDesign={() => abrirConfiguradorCon('Novios')} />
        <StockDisponible items={stockItems} onBuy={handleStockBuy} />
        <Reseñas />
        <FAQ />
        <SobreNosotros />
        <Suscribete />
      </main>
      
      <Footer logoUrl={footerLogoUrl} socialLinks={socialLinks} onWhatsAppClick={() => track('whatsapp_click', { source: 'footer' })} />

      {/* Floating WhatsApp button — envuelto en aside para landmark a11y */}
      <aside aria-label="Contacto rápido">
      <a
        href="https://wa.me/34627602815?text=Hola%2C%20me%20interesa%20una%20figura%20de%20comuni%C3%B3n"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track('whatsapp_click', { source: 'floating' })}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-110 hover:shadow-xl transition-all group"
        aria-label="Contactar por WhatsApp"
      >
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="absolute left-full ml-3 px-3 py-1.5 bg-white rounded-lg shadow-md text-sm text-gray-700 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" aria-hidden="true">
          ¿Dudas? Escríbenos
        </span>
      </a>
      </aside>

      <Suspense fallback={null}>
        {mostrarConfigurador && (
          <div
            className="fixed inset-0 z-[60] overflow-y-auto"
            style={{ backgroundColor: 'var(--page-bg)' }}
            role="dialog"
            aria-modal="true"
            aria-label={t('home.configureFigure')}
          >
            <div className="container mx-auto px-6 py-8 pt-8">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-semibold" style={{ color: 'var(--page-heading)' }}>{t('home.configureFigure')}</h2>
                <button
                  type="button"
                  onClick={cerrarConfigurador}
                  className="w-10 h-10 rounded-full border border-[#D4AF37]/50 flex items-center justify-center hover:bg-[#D4AF37]/10 transition-colors"
                  aria-label={t('common.close')}
                >
                  <span aria-hidden="true">✕</span>
                </button>
              </div>
              {/* key: si se descarta un borrador ya restaurado para empezar otro tipo, el wizard debe arrancar de cero */}
              <ConfiguradorWizard key={initialTipo || 'wizard'} onComplete={handleConfigComplete} initialTipo={initialTipo} />
            </div>
          </div>
        )}

        {mostrarAuth && (
          <AuthModal
            onClose={() => setMostrarAuth(false)}
            onAuthSuccess={handleAuthSuccess}
            modoInicial={pendingConfig ? 'registro' : 'login'}
          />
        )}

        {mostrarDashboard && isLoggedIn && (
          <UserDashboard
            user={currentUser}
            onClose={() => { setMostrarDashboard(false); setDashboardTab(undefined); setDashboardOrderId(undefined); }}
            onNewOrder={() => { clearTempSession(); setMostrarDashboard(false); setMostrarConfigurador(true); }}
            initialTab={dashboardTab}
            initialOrderId={dashboardOrderId}
            onNotificationsRead={() => setUnreadCount(0)}
          />
        )}
      </Suspense>

      {/* Listener de propuestas Classic Plus para anónimos activos.
          Cuando el admin envía una propuesta, aparece un toast con la imagen
          y al pulsar "Quiero esta versión" se aplica al wizard. */}
      <AnonProposalListener
        disabled={isLoggedIn}
        onRedeem={(p) => {
          // Guardar la propuesta en sessionStorage para que el wizard la
          // recoja al iniciar (consume en useEffect del Wizard).
          try {
            sessionStorage.setItem('funcomunion_pending_proposal', JSON.stringify({
              id: p.id,
              imageUrl: p.imageUrl,
              price: p.price,
              proposalType: p.proposalType || 'classicPlus',
            }));
          } catch {}
          // Abre el wizard. El wizard lee la propuesta y aplica config.
          setMostrarConfigurador(true);
        }}
      />

      {exitModal && (
        <ExitIntentModal
          kind={exitModal}
          onClose={() => setExitModal(null)}
          onShare={(canal) => trackEvento('referral_share', { canal })}
          onResume={() => {
            trackEvento('exit_modal_resume', { kind: exitModal });
            setExitModal(null);
            setMostrarConfigurador(true);
          }}
        />
      )}
    </div>
  );
}
